import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import {
  FileText, Plus, Send, Users, BarChart3, Sparkles, Mail, Loader2, Save, Trash2,
  Download, Copy, CheckCircle2, AlertTriangle, ExternalLink, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  ['dashboard', 'Overview', BarChart3],
  ['posts', 'Articles', FileText],
  ['social', 'Social', Sparkles],
  ['newsletter', 'Newsletter', Mail],
  ['waitlist', 'Waitlist', Users],
];

const EMPTY = {
  title: '', subtitle: '', summary: '', content: '', category: 'Agentic FuturePath AI Updates',
  tags: [], featured_image: '', featured_image_alt: '', author_name: '', author_bio: '',
  featured: false, cta_label: 'Join the waitlist', cta_href: '/waitlist',
  seo: { seo_title: '', meta_description: '', slug: '', target_keyword: '', secondary_keywords: [],
         og_title: '', og_description: '', og_image: '', canonical_url: '', no_index: false },
};

const inputCls = 'w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-300';
const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider text-slate-300 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-300 mt-1">{hint}</p>}
  </div>
);

export default function AdminContentPage() {
  const { getAuthHeader } = useAuth();
  const auth = getAuthHeader ? getAuthHeader() : {};
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [socialDrafts, setSocialDrafts] = useState([]);
  const [socialOpts, setSocialOpts] = useState({ tones: [], audiences: [] });
  const [socialCfg, setSocialCfg] = useState({ tone: 'Professional', audience: 'students and families', campaign: '' });
  const [newsletters, setNewsletters] = useState([]);
  const [segments, setSegments] = useState([]);
  const [leads, setLeads] = useState({ total: 0, leads: [] });
  const [leadQuery, setLeadQuery] = useState('');
  const [analytics, setAnalytics] = useState(null);

  const loadDash = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        axios.get(`${API}/admin/content/dashboard`, auth),
        axios.get(`${API}/admin/analytics/summary`, auth),
      ]);
      setDash(d.data); setAnalytics(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the content dashboard.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPosts = useCallback(async () => {
    const r = await axios.get(`${API}/admin/blog/posts`, auth);
    setPosts(r.data.posts || []); setCategories(r.data.categories || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDash(); loadPosts();
    axios.get(`${API}/admin/social/options`, auth).then((r) => setSocialOpts(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'social') axios.get(`${API}/admin/social/drafts`, auth).then((r) => setSocialDrafts(r.data.drafts)).catch(() => {});
    if (tab === 'newsletter') axios.get(`${API}/admin/newsletter`, auth).then((r) => { setNewsletters(r.data.newsletters); setSegments(r.data.segments); }).catch(() => {});
    if (tab === 'waitlist') axios.get(`${API}/admin/waitlist`, { ...auth, params: { q: leadQuery || undefined } }).then((r) => setLeads(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, leadQuery]);

  const openPost = async (id) => {
    if (!id) { setEditing({ ...EMPTY }); setAudit(null); setTab('posts'); return; }
    const r = await axios.get(`${API}/admin/blog/posts/${id}`, auth);
    setEditing(r.data.post); setAudit(r.data.seo_audit);
  };

  const savePost = async () => {
    setBusy(true);
    try {
      const body = { ...editing, tags: editing.tags || [], seo: editing.seo || {} };
      const r = editing.id
        ? await axios.put(`${API}/admin/blog/posts/${editing.id}`, body, auth)
        : await axios.post(`${API}/admin/blog/posts`, body, auth);
      toast.success(editing.id ? 'Article saved' : 'Draft created');
      setEditing(r.data.post);
      const fresh = await axios.get(`${API}/admin/blog/posts/${r.data.post.id}`, auth);
      setAudit(fresh.data.seo_audit);
      loadPosts(); loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the article.');
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status, scheduled_for = null) => {
    try {
      await axios.post(`${API}/admin/blog/posts/${id}/status`, { status, scheduled_for }, auth);
      toast.success(`Article ${status}`);
      loadPosts(); loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change the status.');
    }
  };

  const removePost = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    await axios.delete(`${API}/admin/blog/posts/${id}`, auth);
    toast.success('Article deleted');
    if (editing?.id === id) setEditing(null);
    loadPosts(); loadDash();
  };

  const genSocial = async (postId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/blog/posts/${postId}/social`, socialCfg, auth);
      setSocialDrafts((d) => [r.data.draft, ...d]);
      setTab('social');
      toast.success('Social content generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed.');
    } finally { setBusy(false); }
  };

  const genNewsletter = async (postId, segment = 'all') => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletter/generate`, { post_id: postId, segment }, auth);
      setNewsletters((n) => [r.data.newsletter, ...n]);
      setTab('newsletter');
      toast.success('Newsletter drafted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed.');
    } finally { setBusy(false); }
  };

  const sendNewsletter = async (id, testEmail) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletter/${id}/send`,
        testEmail ? { test_email: testEmail } : {}, auth);
      toast.success(testEmail ? 'Test email sent' : `Sent to ${r.data.sent} subscribers`);
      axios.get(`${API}/admin/newsletter`, auth).then((res) => setNewsletters(res.data.newsletters));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const exportCsv = async () => {
    const r = await axios.get(`${API}/admin/waitlist/export`, { ...auth, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'futurepath-waitlist.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-6" data-testid="admin-content-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold">Growth workspace</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white">Content & Marketing</h1>
          <p className="text-slate-300 text-sm mt-1">
            Publish FuturePath Insights, grow the waitlist, repurpose articles and track what works.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`content-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${
                tab === id ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-400/40' : 'border border-slate-700 text-slate-300 hover:text-slate-200'}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* ── Overview ─────────────────────────────────────────── */}
        {tab === 'dashboard' && dash && (
          <div className="space-y-5" data-testid="content-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[['Waitlist total', dash.waitlist_total], ['New (7 days)', dash.waitlist_last_7_days],
                ['Published', dash.posts_by_status?.published || 0], ['Drafts', dash.posts_by_status?.draft || 0],
                ['Article views (30d)', analytics?.article_views || 0], ['Signups (30d)', analytics?.waitlist_signups || 0],
                ['Conversion', `${analytics?.conversion_rate || 0}%`], ['Social drafts', dash.social_drafts]].map(([k, v]) => (
                <div key={k} className="glass-card p-4">
                  <p className="text-[11px] uppercase tracking-wider text-slate-300">{k}</p>
                  <p className="font-outfit text-2xl font-bold text-white mt-1">{v}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="glass-card p-5">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Top articles</h3>
                <ul className="mt-3 space-y-2">
                  {(dash.top_articles || []).map((p) => (
                    <li key={p.slug} className="flex justify-between gap-3 text-sm">
                      <a href={`/insights/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-cyan-300 truncate">{p.title}</a>
                      <span className="text-slate-300 flex-shrink-0">{p.views || 0} views</span>
                    </li>
                  ))}
                  {!(dash.top_articles || []).length && <p className="text-sm text-slate-300">No published articles yet.</p>}
                </ul>
              </div>
              <div className="glass-card p-5">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Needs attention</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {(dash.low_seo_articles || []).map((p) => (
                    <li key={p.id} className="flex items-center gap-2 text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5" /> {p.title}, SEO {p.score}%
                    </li>
                  ))}
                  {(dash.content_needing_update || []).map((p) => (
                    <li key={p.slug} className="text-slate-300">Stale: {p.title}</li>
                  ))}
                  {!(dash.low_seo_articles || []).length && !(dash.content_needing_update || []).length && (
                    <p className="text-sm text-slate-300">Nothing flagged. Nice.</p>
                  )}
                </ul>
              </div>
            </div>

            {analytics && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {[['Events', analytics.by_event], ['Top pages', analytics.top_paths], ['Campaigns', analytics.by_campaign]].map(([label, rows]) => (
                  <div key={label} className="glass-card p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">{label}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {(rows || []).map((r) => (
                        <li key={r.key} className="flex justify-between gap-3 text-sm">
                          <span className="text-slate-300 truncate">{r.key}</span>
                          <span className="text-slate-300">{r.count}</span>
                        </li>
                      ))}
                      {!(rows || []).length && <p className="text-sm text-slate-300">No data yet.</p>}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Articles ─────────────────────────────────────────── */}
        {tab === 'posts' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="content-posts">
            <div className="glass-card p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Articles</h3>
                <button onClick={() => openPost(null)} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="new-post-btn">
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
              </div>
              <div className="mt-3 space-y-2 max-h-[70vh] overflow-y-auto">
                {posts.map((p) => (
                  <div key={p.id} className={`rounded-xl border p-3 cursor-pointer transition-colors duration-200 ${editing?.id === p.id ? 'border-cyan-400/50 bg-cyan-500/5' : 'border-slate-700/70 bg-slate-900/40 hover:border-cyan-500/40'}`}
                    onClick={() => openPost(p.id)} data-testid={`post-row-${p.id}`}>
                    <p className="text-sm text-white leading-snug">{p.title}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px]">
                      <span className={`px-2 py-0.5 rounded-full ${p.status === 'published' ? 'bg-emerald-500/15 text-emerald-300' : p.status === 'scheduled' ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-700/40 text-slate-300'}`}>{p.status}</span>
                      <span className="text-slate-300">{p.category}</span>
                      {p.seo_audit && <span className={p.seo_audit.score >= 70 ? 'text-emerald-300' : 'text-amber-300'}>SEO {p.seo_audit.score}%</span>}
                    </div>
                  </div>
                ))}
                {!posts.length && <p className="text-sm text-slate-300">No articles yet, create your first one.</p>}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              {!editing && <div className="glass-card p-8 text-center text-slate-300">Pick an article, or create a new one.</div>}
              {editing && (
                <>
                  <div className="glass-card p-5 space-y-3">
                    <Field label="Title"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="post-title" /></Field>
                    <Field label="Subtitle"><input className={inputCls} value={editing.subtitle || ''} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="post-subtitle" /></Field>
                    <Field label="Summary" hint="Shown on cards, search results and social previews.">
                      <textarea className={inputCls} rows={2} value={editing.summary || ''} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} data-testid="post-summary" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Category">
                        <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="post-category">
                          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Tags (comma separated)">
                        <input className={inputCls} value={(editing.tags || []).join(', ')}
                          onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} data-testid="post-tags" />
                      </Field>
                      <Field label="Featured image URL"><input className={inputCls} value={editing.featured_image || ''} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} data-testid="post-image" /></Field>
                      <Field label="Image alt text"><input className={inputCls} value={editing.featured_image_alt || ''} onChange={(e) => setEditing({ ...editing, featured_image_alt: e.target.value })} data-testid="post-image-alt" /></Field>
                      <Field label="Author name"><input className={inputCls} value={editing.author_name || ''} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} data-testid="post-author" /></Field>
                      <Field label="Author bio"><input className={inputCls} value={editing.author_bio || ''} onChange={(e) => setEditing({ ...editing, author_bio: e.target.value })} /></Field>
                      <Field label="CTA label"><input className={inputCls} value={editing.cta_label || ''} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} /></Field>
                      <Field label="CTA link"><input className={inputCls} value={editing.cta_href || ''} onChange={(e) => setEditing({ ...editing, cta_href: e.target.value })} /></Field>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="post-featured" />
                      Feature this article at the top of Insights
                    </label>
                    <Field label="Content (HTML)" hint="Use <h2>, <p>, <ul>, <blockquote>, <a href>, <img>, <iframe> for embeds.">
                      <textarea className={`${inputCls} font-mono text-xs`} rows={14} value={editing.content || ''} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="post-content" />
                    </Field>
                  </div>

                  <div className="glass-card p-5 space-y-3">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">SEO</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[['seo_title', 'SEO title'], ['slug', 'URL slug'], ['target_keyword', 'Target keyword'],
                        ['og_title', 'Social title'], ['og_image', 'Social image URL'], ['canonical_url', 'Canonical URL']].map(([k, label]) => (
                        <Field key={k} label={label}>
                          <input className={inputCls} value={editing.seo?.[k] || ''}
                            onChange={(e) => setEditing({ ...editing, seo: { ...(editing.seo || {}), [k]: e.target.value } })}
                            data-testid={`seo-${k}`} />
                        </Field>
                      ))}
                    </div>
                    <Field label="Meta description">
                      <textarea className={inputCls} rows={2} value={editing.seo?.meta_description || ''}
                        onChange={(e) => setEditing({ ...editing, seo: { ...(editing.seo || {}), meta_description: e.target.value } })} data-testid="seo-meta_description" />
                    </Field>
                    <Field label="Secondary keywords (comma separated)">
                      <input className={inputCls} value={(editing.seo?.secondary_keywords || []).join(', ')}
                        onChange={(e) => setEditing({ ...editing, seo: { ...(editing.seo || {}), secondary_keywords: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) } })} />
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={!!editing.seo?.no_index}
                        onChange={(e) => setEditing({ ...editing, seo: { ...(editing.seo || {}), no_index: e.target.checked } })} />
                      No-index this article
                    </label>

                    {audit && (
                      <div className="rounded-xl border border-slate-700/70 bg-slate-900/40 p-4" data-testid="seo-audit">
                        <p className={`text-sm font-semibold ${audit.score >= 70 ? 'text-emerald-300' : 'text-amber-300'}`}>
                          SEO quality {audit.score}% · {audit.word_count} words · {audit.reading_time} min read
                        </p>
                        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {audit.checks.map((c) => (
                            <li key={c.key} className={`text-xs flex items-center gap-1.5 ${c.pass ? 'text-emerald-300/80' : 'text-slate-300'}`}>
                              {c.pass ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3 text-amber-400" />}
                              {c.label}{c.value !== undefined ? ` (${c.value})` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={savePost} disabled={busy} className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="save-post-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                    </button>
                    {editing.id && (
                      <>
                        <a href={`/insights/${editing.slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-300 hover:text-cyan-300 inline-flex items-center gap-1" data-testid="preview-post-link">
                          <ExternalLink className="w-3.5 h-3.5" /> Preview
                        </a>
                        <button onClick={() => setStatus(editing.id, 'published')} className="text-xs font-semibold text-emerald-300 hover:text-emerald-200" data-testid="publish-post-btn">Publish</button>
                        <button onClick={() => setStatus(editing.id, 'draft')} className="text-xs text-slate-300 hover:text-white">Unpublish</button>
                        <input type="datetime-local" className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                          onChange={(e) => e.target.value && setStatus(editing.id, 'scheduled', new Date(e.target.value).toISOString())} data-testid="schedule-post-input" />
                        <button onClick={() => setStatus(editing.id, 'archived')} className="text-xs text-slate-300 hover:text-white">Archive</button>
                        <button onClick={() => genSocial(editing.id)} disabled={busy} className="text-xs font-semibold text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="generate-social-btn">
                          <Sparkles className="w-3.5 h-3.5" /> Generate Social Content
                        </button>
                        <button onClick={() => genNewsletter(editing.id)} disabled={busy} className="text-xs font-semibold text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="generate-newsletter-btn">
                          <Mail className="w-3.5 h-3.5" /> Draft newsletter
                        </button>
                        <button onClick={() => removePost(editing.id)} className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 ml-auto" data-testid="delete-post-btn">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Social ───────────────────────────────────────────── */}
        {tab === 'social' && (
          <div className="space-y-5" data-testid="content-social">
            <div className="glass-card p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Tone">
                <select className={inputCls} value={socialCfg.tone} onChange={(e) => setSocialCfg({ ...socialCfg, tone: e.target.value })} data-testid="social-tone">
                  {(socialOpts.tones || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Audience">
                <select className={inputCls} value={socialCfg.audience} onChange={(e) => setSocialCfg({ ...socialCfg, audience: e.target.value })} data-testid="social-audience">
                  {(socialOpts.audiences || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Campaign tag (UTM)"><input className={inputCls} value={socialCfg.campaign} onChange={(e) => setSocialCfg({ ...socialCfg, campaign: e.target.value })} data-testid="social-campaign" /></Field>
              <p className="sm:col-span-3 text-xs text-slate-300">
                Pick an article in the Articles tab and hit “Generate Social Content”. Nothing is auto-posted.
              </p>
            </div>

            {socialDrafts.map((d) => (
              <div key={d.id} className="glass-card p-5" data-testid={`social-draft-${d.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-semibold text-white">{d.post_title}</p>
                  <span className="text-[11px] text-slate-300">{d.tone} · {d.audience}</span>
                  <button onClick={() => genSocial(d.post_id)} className="text-xs text-cyan-300 hover:text-cyan-200 ml-auto">Regenerate</button>
                </div>
                <div className="mt-3 space-y-3">
                  {[['LinkedIn (long)', d.content.linkedin_long], ['LinkedIn (short)', d.content.linkedin_short],
                    ['Facebook', d.content.facebook], ['Instagram', d.content.instagram],
                    ['Newsletter summary', d.content.newsletter_summary], ['Call to action', d.content.call_to_action]]
                    .filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-slate-700/70 bg-slate-900/40 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-wider text-slate-300">{label}</p>
                        <button onClick={() => copy(value)} className="text-slate-300 hover:text-cyan-300"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{value}</p>
                    </div>
                  ))}
                  {[['X posts', d.content.x_posts], ['X thread', d.content.x_thread],
                    ['Headlines', d.content.headlines], ['Hooks', d.content.hooks], ['Hashtags', d.content.hashtags]]
                    .filter(([, v]) => (v || []).length).map(([label, items]) => (
                    <div key={label} className="rounded-xl border border-slate-700/70 bg-slate-900/40 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-wider text-slate-300">{label}</p>
                        <button onClick={() => copy(items.join('\n\n'))} className="text-slate-300 hover:text-cyan-300"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <ol className="mt-1 space-y-1.5">
                        {items.map((it, i) => <li key={i} className="text-sm text-slate-200">{it}</li>)}
                      </ol>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => axios.patch(`${API}/admin/social/drafts/${d.id}`, { posted: { ...(d.posted || {}), all: new Date().toISOString() } }, auth).then(() => toast.success('Marked as posted'))}
                    className="text-xs text-emerald-300 hover:text-emerald-200" data-testid={`mark-posted-${d.id}`}>Mark as posted</button>
                  <a href={d.tracked_url} target="_blank" rel="noreferrer" className="text-xs text-slate-300 hover:text-cyan-300">Tracked link</a>
                </div>
              </div>
            ))}
            {!socialDrafts.length && <div className="glass-card p-8 text-center text-slate-300">No social drafts yet.</div>}
          </div>
        )}

        {/* ── Newsletter ───────────────────────────────────────── */}
        {tab === 'newsletter' && (
          <div className="space-y-5" data-testid="content-newsletter">
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Audience segments</h3>
              <div className="flex flex-wrap gap-2 mt-3">
                {segments.map((s) => (
                  <span key={s.key} className="text-xs px-3 py-1.5 rounded-full bg-slate-800/70 text-slate-300">
                    {s.label} · {s.count}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-300 mt-3">
                Draft a newsletter from any article in the Articles tab. Every send includes an unsubscribe link and a privacy link.
              </p>
            </div>

            {newsletters.map((n) => (
              <div key={n.id} className="glass-card p-5 space-y-3" data-testid={`newsletter-${n.id}`}>
                <Field label="Subject">
                  <input className={inputCls} defaultValue={n.subject}
                    onBlur={(e) => axios.put(`${API}/admin/newsletter/${n.id}`, { subject: e.target.value }, auth)} data-testid="newsletter-subject" />
                </Field>
                <Field label="Preview text">
                  <input className={inputCls} defaultValue={n.preview_text}
                    onBlur={(e) => axios.put(`${API}/admin/newsletter/${n.id}`, { preview_text: e.target.value }, auth)} />
                </Field>
                <Field label="Body (HTML)">
                  <textarea className={`${inputCls} font-mono text-xs`} rows={8} defaultValue={n.body_html}
                    onBlur={(e) => axios.put(`${API}/admin/newsletter/${n.id}`, { body_html: e.target.value }, auth)} data-testid="newsletter-body" />
                </Field>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${n.status === 'sent' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700/40 text-slate-300'}`}>{n.status}</span>
                  <span className="text-[11px] text-slate-300">Segment: {n.segment}</span>
                  <span className="text-[11px] text-slate-300">
                    {n.stats?.sent || 0} sent · {n.stats?.opens || 0} opens · {n.stats?.clicks || 0} clicks
                  </span>
                  <input placeholder="test@email.com" className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                    id={`test-${n.id}`} data-testid={`newsletter-test-email-${n.id}`} />
                  <button onClick={() => sendNewsletter(n.id, document.getElementById(`test-${n.id}`).value)}
                    className="text-xs text-cyan-300 hover:text-cyan-200" data-testid={`newsletter-test-send-${n.id}`}>Send test</button>
                  <button onClick={() => window.confirm('Send to the whole segment?') && sendNewsletter(n.id, null)}
                    className="btn-primary inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold" data-testid={`newsletter-send-${n.id}`}>
                    <Send className="w-3.5 h-3.5" /> Send
                  </button>
                </div>
              </div>
            ))}
            {!newsletters.length && <div className="glass-card p-8 text-center text-slate-300">No newsletters drafted yet.</div>}
          </div>
        )}

        {/* ── Waitlist ─────────────────────────────────────────── */}
        {tab === 'waitlist' && (
          <div className="space-y-4" data-testid="content-waitlist">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={leadQuery} onChange={(e) => setLeadQuery(e.target.value)} placeholder="Search name, email or organization"
                  className={`${inputCls} pl-9`} data-testid="waitlist-search" />
              </div>
              <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 text-xs font-semibold text-slate-300 hover:border-cyan-500/40" data-testid="waitlist-export-btn">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
              <span className="text-xs text-slate-300">{leads.total} leads</span>
            </div>
            <div className="glass-card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-300 border-b border-slate-800">
                    <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Audience</th>
                    <th className="p-3">Organization</th><th className="p-3">Source</th><th className="p-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.leads.map((l) => (
                    <tr key={l.id} className="border-b border-slate-800/60" data-testid={`lead-row-${l.id}`}>
                      <td className="p-3 text-white">{l.first_name} {l.last_name}</td>
                      <td className="p-3 text-slate-300">{l.email}</td>
                      <td className="p-3 text-slate-300">{(l.role_interest || '').replace(/_/g, ' ')}</td>
                      <td className="p-3 text-slate-300">{l.organization}</td>
                      <td className="p-3 text-slate-300">{l.source_page || l.source}</td>
                      <td className="p-3 text-slate-300">{(l.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!leads.leads.length && <p className="p-6 text-center text-sm text-slate-300">No leads yet.</p>}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
